import type { MedplumClient } from '@medplum/core';
import type {
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireResponse,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes';
import { EPISODE_OF_CARE_CONTEXT_EXTENSION } from '@/lib/caseloadFhirConstants';
import {
  QR_INTERPRETATION_EXTENSION,
  QR_TOTAL_SCORE_EXTENSION,
  resolveInterpretationLabel,
} from '@/lib/assessmentMedplumAdapters';
import { appointmentTag } from '@/pages/appointments/fhirNoteMapping';
import { getItemWeight, setItemWeight } from '@/pages/settings/template-library/utils/scoringUtils';
import { DETAILED_STATUS_EXT_URL } from '@/pages/appointments/fhirNoteMapping';

function scorableItems(q: Questionnaire): QuestionnaireItem[] {
  return (q.item ?? []).filter(
    (it) => (it.type === 'choice' || it.type === 'open-choice') && it.answerOption?.length,
  );
}

function optionScoreWeight(
  option: NonNullable<QuestionnaireItem['answerOption']>[number],
  index: number,
): number {
  return getItemWeight(option) ?? option.valueInteger ?? index;
}

function buildMeasureItems(
  questionnaire: Questionnaire,
  answers: Record<string, number>,
): QuestionnaireResponse['item'] {
  return scorableItems(questionnaire).map((item) => {
    const weight = answers[item.linkId!];
    const optIndex = (item.answerOption ?? []).findIndex(
      (o, idx) => optionScoreWeight(o, idx) === weight,
    );
    const opt = optIndex >= 0 ? item.answerOption![optIndex] : undefined;
    if (opt?.valueCoding) {
      // Ensure itemWeight travels on the stored coding so history/bots can re-sum.
      const withWeight = weight != null ? setItemWeight(opt, weight) : opt;
      return { linkId: item.linkId!, answer: [{ valueCoding: withWeight.valueCoding }] };
    }
    return {
      linkId: item.linkId!,
      answer: [{ valueInteger: Math.round(weight) }],
    };
  });
}

export function computeInSessionAssessmentTotal(answers: Record<string, number>): number {
  return Object.values(answers).reduce((sum, v) => sum + (Number.isFinite(v) ? v : 0), 0);
}

export async function saveInSessionAssessment(input: {
  medplum: MedplumClient;
  questionnaire: Questionnaire;
  patientId: string;
  episodeId?: string;
  appointmentId?: string;
  encounterRef?: string;
  answers: Record<string, number>;
  author?: Reference<Practitioner>;
}): Promise<QuestionnaireResponse> {
  const { medplum, questionnaire, patientId, episodeId, appointmentId, encounterRef, answers, author } =
    input;

  const canonicalRef =
    questionnaire.url && questionnaire.version
      ? `${questionnaire.url}|${questionnaire.version}`
      : `Questionnaire/${questionnaire.id}`;

  const totalScore = Math.round(computeInSessionAssessmentTotal(answers));
  const extensions: NonNullable<QuestionnaireResponse['extension']> = [
    { url: DETAILED_STATUS_EXT_URL, valueCode: 'completed' },
    { url: QR_TOTAL_SCORE_EXTENSION, valueInteger: totalScore },
  ];
  if (episodeId) {
    extensions.push({
      url: EPISODE_OF_CARE_CONTEXT_EXTENSION,
      valueReference: { reference: `EpisodeOfCare/${episodeId}` },
    });
  }

  const draftQr: QuestionnaireResponse = {
    resourceType: 'QuestionnaireResponse',
    status: 'completed',
    authored: new Date().toISOString(),
    subject: { reference: `Patient/${patientId}` },
    questionnaire: canonicalRef,
    item: buildMeasureItems(questionnaire, answers),
    extension: extensions,
  };

  const interpretation = resolveInterpretationLabel({
    qr: draftQr,
    questionnaire,
    score: totalScore,
    observation: undefined,
  });
  if (interpretation) {
    extensions.push({ url: QR_INTERPRETATION_EXTENSION, valueString: interpretation });
  }

  const qr: QuestionnaireResponse = {
    ...draftQr,
    extension: extensions,
    ...(encounterRef ? { encounter: { reference: encounterRef } } : {}),
    ...(author ? { author, source: author } : {}),
    ...(appointmentId
      ? {
          meta: {
            tag: [appointmentTag(appointmentId)],
          },
        }
      : {}),
  };

  return medplum.createResource(qr);
}
